import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from './config';

// Collection names
const COLLECTIONS = {
  CHOCOLATE_CAKES: 'chocolate_cakes',
  PINEAPPLE_CAKES: 'pineapple_cakes',
  CUSTOMIZED_CAKES: 'customized_cakes',
  JAR_CAKES: 'jar_cakes',
  BROWNIES: 'brownies'
};

// Helper function to get collection name
const getCollectionName = (category, subCategory = null) => {
  if (category === 'cakes' && subCategory) {
    return COLLECTIONS[`${subCategory.toUpperCase()}_CAKES`];
  }
  if (category === 'jarCakes') return COLLECTIONS.JAR_CAKES;
  if (category === 'brownies') return COLLECTIONS.BROWNIES;
  return category;
};

// Compress and convert image to base64
export const uploadImage = async (file, productName) => {
  // If it's already a base64 string, return it
  if (typeof file === 'string' && file.startsWith('data:image')) {
    return file;
  }
  
  // If it's a File object, compress and convert
  if (file instanceof File) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        
        img.onload = () => {
          // Create canvas for compression
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Set max dimensions (optimize for web)
          const MAX_WIDTH = 800;
          const MAX_HEIGHT = 800;
          
          let width = img.width;
          let height = img.height;
          
          // Calculate new dimensions
          if (width > height) {
            if (width > MAX_WIDTH) {
              height = height * (MAX_WIDTH / width);
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width = width * (MAX_HEIGHT / height);
              height = MAX_HEIGHT;
            }
          }
          
          // Set canvas size and draw image
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to base64 with compression (0.7 quality = 70%)
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
          
          resolve(compressedBase64);
        };
        
        img.onerror = reject;
        img.src = e.target.result;
      };
      
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
  
  return file;
};

// Fetch all products
export const fetchAllProducts = async () => {
  try {
    const products = {
      cakes: {
        chocolate: [],
        pineapple: [],
        customized: []
      },
      jarCakes: [],
      brownies: []
    };

    // Fetch chocolate cakes
    const chocolateSnapshot = await getDocs(collection(db, COLLECTIONS.CHOCOLATE_CAKES));
    products.cakes.chocolate = chocolateSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Fetch pineapple cakes
    const pineappleSnapshot = await getDocs(collection(db, COLLECTIONS.PINEAPPLE_CAKES));
    products.cakes.pineapple = pineappleSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Fetch customized cakes
    const customizedSnapshot = await getDocs(collection(db, COLLECTIONS.CUSTOMIZED_CAKES));
    products.cakes.customized = customizedSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Fetch jar cakes
    const jarCakesSnapshot = await getDocs(collection(db, COLLECTIONS.JAR_CAKES));
    products.jarCakes = jarCakesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Fetch brownies
    const browniesSnapshot = await getDocs(collection(db, COLLECTIONS.BROWNIES));
    products.brownies = browniesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return products;
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

// Add product
export const addProductToFirebase = async (category, productData, subCategory = null) => {
  try {
    const collectionName = getCollectionName(category, subCategory);
    
    const productWithTimestamp = {
      ...productData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, collectionName), productWithTimestamp);
    
    return {
      id: docRef.id,
      ...productData
    };
  } catch (error) {
    console.error('Error adding product:', error);
    throw error;
  }
};

// Update product
export const updateProductInFirebase = async (category, productId, productData, subCategory = null) => {
  try {
    const collectionName = getCollectionName(category, subCategory);
    const productRef = doc(db, collectionName, productId);
    
    const updatedData = {
      ...productData,
      updatedAt: serverTimestamp()
    };

    await updateDoc(productRef, updatedData);
    
    return {
      id: productId,
      ...productData
    };
  } catch (error) {
    console.error('Error updating product:', error);
    throw error;
  }
};

// Delete product
export const deleteProductFromFirebase = async (category, productId, subCategory = null) => {
  try {
    const collectionName = getCollectionName(category, subCategory);
    const productRef = doc(db, collectionName, productId);
    
    await deleteDoc(productRef);
    
    return productId;
  } catch (error) {
    console.error('Error deleting product:', error);
    throw error;
  }
};